import { cloneDeep, sortBy } from "lodash-es";
import { isStep } from "@/flow/utils/isStep";

export type Step = {
  actions: any;
  active: boolean;
  connection: any;
  connectorSpecId: string;
  contentPath: string;
  createOrgId: string;
  createdAt: string;
  createdBy: string;
  deleted: boolean;
  filter: [];
  flow: any;
  flowId: string;
  flowVersion: number;
  id: string;
  ix: number;
  iy: number;
  modifiedBy: string;
  name: string;
  output: string;
  stepNumber?: number;
  order?: number;
  translations: [];
  updatedAt: string;
};

export type Route = {
  actions: any;
  active: true;
  createOrgId: string;
  createdAt: string;
  createdBy: string;
  deleted: boolean;
  flow: any;
  flowId: string;
  flowVersion: string;
  id: string;
  ix: number;
  iy: number;
  name: string;
  routes: Condition[];
  stepNumber?: number;
  order?: number;
  translations: [];
  type: "ROUTER";
  updatedAt: string;
};

export type Flow = {
  active: boolean;
  activities: [];
  createOrgId: string;
  createdAt: string;
  deleted: boolean;
  flowStatusType: string;
  newUi?: boolean;
  nextStepNumber?: number;
  id: string;
  latest: boolean;
  modifiedBy: string;
  name: string;
  scheduleType: string;
  steps: Array<Step | Route>;
};

export type Condition = {
  actions: any;
  active: boolean;
  createOrgId: string;
  createdAt: string;
  createdBy: string;
  deleted: boolean;
  filter: [];
  id: string;
  ix: number;
  iy: number;
  order?: number;
  name: string;
  parentId: string;
  steps: Array<Step | Route>;
  translations: [];
  updatedAt: string;
};

function hasExplicitOrder(step: Step | Route): boolean {
  return typeof step?.order === "number";
}

function sortSteps(
  steps: Array<Step | Route>,
  usePositionalIxFallback: boolean
): Array<Step | Route> {
  return sortBy(
    steps.map((step, index) => ({ step, index })),
    [
      function ({ step }: { step: Step | Route; index: number }) {
        if (usePositionalIxFallback) {
          return typeof step?.ix === "number"
            ? step.ix
            : Number.MAX_SAFE_INTEGER;
        }
        if (hasExplicitOrder(step)) {
          return step.order as number;
        }
        return Number.MAX_SAFE_INTEGER;
      },
      function ({ index }: { step: Step | Route; index: number }) {
        return index;
      },
    ]
  ).map(({ step }) => step);
}

export function sortedByIxAndIy(flow: Flow): Flow {
  const currentFlow: Flow = cloneDeep(flow);
  const shouldUseIxFallback = !currentFlow?.newUi;
  function sorted(data: Flow | Condition): (Step | Route)[] {
    data.steps = sortSteps(data.steps, shouldUseIxFallback);
    data.steps.forEach((item: Route | Step) => {
      if ("type" in item && item.type === "ROUTER") {
        item.routes = sortBy(item.routes, [
          function (y: Step | Route) {
            return y.iy;
          },
        ]);
        item.routes.forEach((condition: Condition) => {
          if (condition) {
            sorted(condition);
          }
        });
      }
    });

    return data.steps;
  }

  currentFlow.steps = sorted(currentFlow);

  return currentFlow;
}

export function findSubFlowByStepUniqueId(
  flow: Flow,
  uniqueId: string
):
  | Array<(Step & { uniqueId: string }) | (Route & { uniqueId: string })>
  | undefined {
  function find(
    data: Flow | Condition
  ):
    | Array<(Step & { uniqueId: string }) | (Route & { uniqueId: string })>
    | undefined {
    let foundSteps:
      | Array<(Step & { uniqueId: string }) | (Route & { uniqueId: string })>
      | undefined;

    // Add safety check for steps
    if (!data.steps || !Array.isArray(data.steps)) {
      return undefined;
    }

    data.steps.forEach(
      (
        stepOrRoute:
          | (Route & { uniqueId: string })
          | (Step & { uniqueId: string })
      ) => {
        if (stepOrRoute.uniqueId === uniqueId && isStep(stepOrRoute)) {
          foundSteps = data.steps as Array<
            (Step & { uniqueId: string }) | (Route & { uniqueId: string })
          >;
        }
        if ("type" in stepOrRoute && stepOrRoute.type === "ROUTER") {
          const route = stepOrRoute as Route;
          // Add safety check for routes
          if (route.routes && Array.isArray(route.routes)) {
            route.routes.forEach((condition: Condition) => {
              if (!foundSteps) {
                const innerFoundFlow = find(condition);
                if (innerFoundFlow) {
                  foundSteps = innerFoundFlow;
                }
              }
            });
          }
        }
      }
    );

    return foundSteps;
  }
  const currentFlow: Flow = cloneDeep(flow);
  return find(currentFlow);
}

export function findStepOrRouteByUniqueId(flow: Flow, id: string) {
  // Add safety check for undefined
  const subFlow = findSubFlowByStepUniqueId(flow, id);
  if (!subFlow) {
    return undefined;
  }
  return subFlow.find((x) => x.uniqueId === id);
}
